import { push } from "connected-react-router";
import { pick } from "lodash";
import { SagaIterator } from "redux-saga";
import { all, fork, put, race, select, take, takeEvery } from "redux-saga/effects";
import { store } from "tsx/store";

import { Cookie } from "constants/cookies";
import { SubscriptionPhase } from "constants/subscription-phase";
import {
  SESSION_STORAGE_SESSION_SOURCE_PARAMS_KEY,
  installAmplitudeWithUserData,
  trackEngagement,
} from "helpers/analytics";
import { isInIframe } from "helpers/app";
import {
  getCredentialsFromCookies,
  getCredentialsFromFragment,
  getDeserializedStateFromFragment,
  saveCredentialCookies,
} from "helpers/authenticate";
import { setCookie } from "helpers/cookies";
import { redirectToAccounts } from "helpers/routing";
import { getSubscriptionPhase } from "helpers/subscription";
import { COUPON_COOKIE_OPTIONS, getDaysLeft } from "helpers/subscription-plans";
import { parseQueryParams, utmParams } from "helpers/url";
import { saveItem } from "services/session-storage";
import { CRUDAction, RequestAction } from "store/entities/actions";
import { AGENT, AgentActions, selectIsAdmin, selectLoggedInAgent } from "store/entities/agents";
import {
  ApplicationSettingsActionNames,
  ApplicationSettingsActions,
} from "store/entities/application-settings/actions";
import { ApplicationsActions } from "store/entities/applications";
import { BillingActionNames, BillingActions, getBilling } from "store/entities/billing";
import { CannedResponseActions } from "store/entities/canned-responses";
import { ContactFormActions } from "store/entities/contact-forms/actions";
import { customFieldActions } from "store/entities/custom-fields/actions";
import { CustomViewsActions } from "store/entities/custom-views/actions";
import { EMAIL_DOMAIN, EmailDomainActions } from "store/entities/email-domains";
import { EmailTemplateActionNames, EmailTemplateActions } from "store/entities/email-template/actions";
import { INTEGRATION, IntegrationsActions, selectLiveChatIntegration } from "store/entities/integrations";
import {
  LICENSE,
  LicenseActions,
  selectDefaultMailboxUsed,
  selectLicenseId,
  selectLicenseSourceParams,
} from "store/entities/license";
import { MailboxesActions } from "store/entities/mailboxes/actions";
import { ReplyAddressActions } from "store/entities/reply-addresses/actions";
import { RulesActions, RulesActionsNames } from "store/entities/rules/actions";
import { SUBSCRIPTION, SubscriptionActions, selectCurrentSubscription } from "store/entities/subscriptions";
import { TagActions } from "store/entities/tags";
import { TEAM, TeamActions } from "store/entities/teams";
import { SessionActionNames, SessionActions } from "store/features/session/actions";
import { IActionWithPayload } from "store/helper";
import { Theme, saveThemeToLocalStorage, setTheme } from "themes/helpers";

import { AppActionNames, AppActions } from "./actions";

declare const window: Window & { dataLayer: unknown[] };

function rememberSessionSourceParams(): void {
  const queryParams = parseQueryParams(location.search);
  const utmQueryParams = pick(queryParams, utmParams);
  if (Object.keys(utmQueryParams).length > 0) {
    saveItem(SESSION_STORAGE_SESSION_SOURCE_PARAMS_KEY, utmQueryParams);
  }
}

function* watchInit(): SagaIterator {
  yield take(AppActionNames.INIT);
  rememberSessionSourceParams();
  yield put(LicenseActions.fetchCollection({}));
}

const couponRegexp = /[a-zA-z0-9-_]+/;

function rememberRecurlyCoupon(): void {
  const queryParams = parseQueryParams(location.search);
  if (queryParams.discount && couponRegexp.test(queryParams.discount as string)) {
    setCookie(Cookie.CouponCode, queryParams.discount as string, {
      days: 7,
      ...COUPON_COOKIE_OPTIONS,
    });
  }
}

function* watchLicenseFetched(): SagaIterator {
  yield take(LICENSE[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS]);
  const licenseId = yield select(selectLicenseId);
  if (!licenseId) {
    yield put(LicenseActions.create({ value: {} }));
    yield take(LICENSE[CRUDAction.CREATE][RequestAction.SUCCESS]);
  }

  yield put(IntegrationsActions.fetchCollection({}));
  yield put(SubscriptionActions.fetchCollection({}));
  yield put(SessionActions.fetchCurrentAgent());
  yield put(AgentActions.fetchCollection({}));
  yield put(TeamActions.fetchCollection({}));
  yield put(TagActions.fetchCollection({}));
  yield put(CustomViewsActions.fetchCollection({}));
  yield put(customFieldActions.fetchCollection());
  yield put(CannedResponseActions.fetchCollection({}));
  yield put(EmailDomainActions.fetchCollection({}));
  yield put(ApplicationsActions.fetchInstalledApps());
  yield put(ApplicationsActions.fetchPrivateApps());
  yield put(ApplicationsActions.fetchInReviewApps());
  yield put(ReplyAddressActions.fetchCollection({}));
  yield put(MailboxesActions.fetchCollection({}));
  yield put(BillingActions.fetchBillingInfo());
  yield put(RulesActions.fetchCollection({}));
  yield put(ContactFormActions.fetchCollection({}));
  yield put(EmailTemplateActions.fetch());
  yield put(ApplicationSettingsActions.fetch());
}

const subscriptionPhasesTrial = [SubscriptionPhase.TrialCC, SubscriptionPhase.TrialNoCC];
const subscriptionPhasesPaid = [SubscriptionPhase.Paid, SubscriptionPhase.PaidNoCC];

function* watchInitDataFetched(): SagaIterator {
  yield all([
    take(SUBSCRIPTION[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS]),
    take(SessionActionNames.SAVE_CURRENT_AGENT),
    take(INTEGRATION[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS]),
    take(AGENT[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS]),
    take(TEAM[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS]),
    take(EMAIL_DOMAIN[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS]),
    take(RulesActionsNames[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS]),
    take(EmailTemplateActionNames.FETCH_EMAIL_TEMPLATE_SUCCESS),
    take(ApplicationSettingsActionNames.FETCH_APPLICATION_SETTINGS_SUCCESS),
    race([take(BillingActionNames.FETCH_BILLING_INFO_SUCCESS), take(BillingActionNames.FETCH_BILLING_INFO_FAILURE)]),
  ]);

  const currentAgent = yield select(selectLoggedInAgent);
  const isAdmin = yield select(selectIsAdmin);
  const licenseId = yield select(selectLicenseId);
  const licenseSource = yield select(selectLicenseSourceParams);
  const currentSubscription = yield select(selectCurrentSubscription);
  const currentBilling = yield select(getBilling);
  const isDefaultMailboxUsed = yield select(selectDefaultMailboxUsed);
  const livechatIntegration = yield select(selectLiveChatIntegration);

  const isInFrame = isInIframe();
  const hasLivechatIntegration = livechatIntegration !== undefined;
  const subscriptionPhase = getSubscriptionPhase(currentSubscription, currentBilling);

  // THIRD SET THEME: change theme just after user settings are fetched, but only for HD standalone, because in iframe we must sync theme with LC
  if (currentAgent && !isInFrame) {
    const currentAgentTheme = currentAgent.settings.theme;
    yield put(AppActions.setTheme(currentAgentTheme));
  }

  if (hasLivechatIntegration && isAdmin && subscriptionPhasesTrial.includes(subscriptionPhase)) {
    yield put(IntegrationsActions.fetchLiveChatSelectiveImport());
  }

  if (hasLivechatIntegration && subscriptionPhasesTrial.includes(subscriptionPhase)) {
    yield put(LicenseActions.fetchLivechatProperties());
  }

  let daysLeft = null;
  if (subscriptionPhasesTrial.includes(subscriptionPhase)) {
    daysLeft = getDaysLeft(currentSubscription);
  } else if (subscriptionPhasesPaid.includes(subscriptionPhase)) {
    daysLeft = 0;
  }

  window.dataLayer.push({
    event: "custom_dimensions",
    license: licenseId,
    licenseType: getSubscriptionPhase(currentSubscription, currentBilling),
    daysLeft,
    forwardingDetected: isDefaultMailboxUsed,
    plan: currentSubscription?.plan,
    role: currentAgent?.permission,
    userID: currentAgent?.id,
  });
  installAmplitudeWithUserData(currentAgent, licenseId, licenseSource, currentSubscription, currentBilling);
  trackEngagement("Logged in");
  store.dispatch(AppActions.ready());
}

function* authenticate(): SagaIterator {
  const credentials = getCredentialsFromFragment() || getCredentialsFromCookies();

  if (credentials) {
    const { redirectTo } = getDeserializedStateFromFragment();
    if (getCredentialsFromFragment()) {
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }
    saveCredentialCookies(credentials);
    store.dispatch(SessionActions.saveAccessToken(credentials.accessToken));
    store.dispatch(SessionActions.saveScopes(credentials.scopes));
    store.dispatch(AppActions.init());

    if (redirectTo) {
      yield put(
        push(`/${decodeURIComponent(redirectTo)}`, {
          redirectFromAccountsState: true,
        })
      );
    }
  } else {
    const isRegisterPathname = ["/register", "/signup"].includes(location.pathname);
    const couponCode = new URLSearchParams(location.search).get("coupon") || undefined;
    redirectToAccounts({ couponCode, signup: isRegisterPathname });
  }
}

// eslint-disable-next-line require-yield
function* setApplicationTheme(action: IActionWithPayload<AppActionNames.SET_THEME, Theme>): SagaIterator {
  const { payload: theme } = action;

  setTheme(theme);

  const inFrame = isInIframe();
  if (!inFrame) {
    saveThemeToLocalStorage(theme);
  }
}

export function* appSagas(): SagaIterator {
  yield all([
    fork(rememberRecurlyCoupon),
    fork(watchLicenseFetched),
    fork(watchInitDataFetched),
    fork(watchInit),
    fork(authenticate),
    takeEvery(AppActionNames.SET_THEME, setApplicationTheme),
  ]);
}
